import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import {
  CalendarCheck,
  Clock,
  Envelope,
  PencilSquare,
  Trash,
  XCircle,
} from "react-bootstrap-icons";

import { ApiConnectionString } from "../constants/ApiConntection";
import { useAuthContext } from "../hooks/useAuthContext";
import LoadingSpinner from "../components/LoadingSpinner";
import "./AppointmentDetailsModal.css";
import "./Modal.css";

export default function AppointmentDetailsModal({
  show,
  appointment,
  onClose,
  onUpdated,
  onDeleted,
}) {
  const { user } = useAuthContext();

  const [mode, setMode] = useState("view");

  const [name, setName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [finishDate, setFinishDate] = useState("");
  const [finishTime, setFinishTime] = useState("");
  const [sendEmail, setSendEmail] = useState(false);
  const [sendCancelEmail, setSendCancelEmail] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [err, setErr] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const timeOptions = useMemo(() => {
    const options = [];

    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const h = String(hour).padStart(2, "0");
        const m = String(minute).padStart(2, "0");
        options.push(`${h}:${m}`);
      }
    }

    return options;
  }, []);

  const getValue = (lower, upper) => {
    return appointment?.[lower] ?? appointment?.[upper] ?? "";
  };

  const appointmentId = getValue("id", "Id");
  const therapySession = getValue("therapySession", "TherapySession");
  const clientId = getValue("clientId", "ClientId");
  const clientName = getValue("clientName", "ClientName");
  const appointmentName = getValue("name", "Name");
  const start = getValue("start", "Start");
  const finish = getValue("finish", "Finish");
  const clientEmail = getValue("clientEmail", "ClientEmail");

  const splitDateTime = (dateTime) => {
    if (!dateTime) {
      return {
        date: "",
        time: "",
      };
    }

    const date = new Date(dateTime);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return {
      date: `${year}-${month}-${day}`,
      time: `${hours}:${minutes}`,
    };
  };

  const buildDateTime = (date, time) => {
    if (!date || !time) return "";
    return `${date}T${time}`;
  };

  const formatDate = (date) => {
    if (!date) return "";

    return new Date(date).toLocaleDateString("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const formatTime = (date) => {
    if (!date) return "";

    return new Date(date).toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  useEffect(() => {
    if (!show || !appointment) return;

    const startParts = splitDateTime(start);
    const finishParts = splitDateTime(finish);

    setName(appointmentName || "");
    setStartDate(startParts.date);
    setStartTime(startParts.time);
    setFinishDate(finishParts.date);
    setFinishTime(finishParts.time);
    setSendEmail(false);
    setSendCancelEmail(false);

    setMode("view");
    setErr("");
    setSuccessMessage("");
  }, [show, appointment]);

  const updateAppointment = async (e) => {
    e.preventDefault();
    setErr("");
    setSuccessMessage("");

    const newStart = buildDateTime(startDate, startTime);
    const newFinish = buildDateTime(finishDate, finishTime);

    if (!newStart || !newFinish) {
      setErr("Please enter a valid start and finish time.");
      return;
    }

    if (new Date(newFinish) <= new Date(newStart)) {
      setErr("Finish time must be after the start time.");
      return;
    }

    setIsLoading(true);

    try {
      const updatedAppointment = {
        Id: appointmentId,
        TherapySession: therapySession,
        ClientId: therapySession ? Number(clientId) : null,
        ClientName: therapySession ? clientName : null,
        Name: therapySession ? "Therapy Session" : name,
        Start: newStart,
        Finish: newFinish,
        SendEmail: therapySession ? sendEmail : false,
      };

      const response = await axios.put(
        `${ApiConnectionString}/api/Appointment/${appointmentId}`,
        updatedAppointment,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      if (onUpdated) {
        onUpdated(response.data);
      }

      setSuccessMessage(
        sendEmail
          ? "Appointment updated and confirmation email sent."
          : "Appointment updated."
      );

      setSendEmail(false);
      setMode("view");
    } catch (error) {
      console.error(error);
      setErr("Failed to update appointment.");
    } finally {
      setIsLoading(false);
    }
  };

  const cancelAppointment = async () => {
    setErr("");
    setSuccessMessage("");
    setIsLoading(true);

    try {
      await axios.delete(
        `${ApiConnectionString}/api/Appointment/${appointmentId}`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
          params: {
            sendEmail: therapySession ? sendCancelEmail : false,
          },
        }
      );

      if (onDeleted) {
        onDeleted(appointmentId);
      }

      onClose();
    } catch (error) {
      console.error(error);
      setErr("Failed to cancel appointment.");
    } finally {
      setIsLoading(false);
    }
  };

  const sendConfirmationEmail = async () => {
    setErr("");
    setSuccessMessage("");
    setIsLoading(true);

    try {
      await axios.post(
        `${ApiConnectionString}/api/Appointment/${appointmentId}/email`,
        {},
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      setSuccessMessage("Appointment confirmation email sent.");
      setMode("view");
    } catch (error) {
      console.error(error);
      setErr("Failed to send appointment email.");
    } finally {
      setIsLoading(false);
    }
  };

  if (!show || !appointment) {
    return null;
  }

  return (
    <div className="modal-backdrop-custom">
      <div className="modal-card-custom">
        <div className="modal-header-custom">
          <div>
            <h2>
              {mode === "view" && "Appointment details"}
              {mode === "edit" && "Edit appointment"}
              {mode === "cancel" && "Cancel appointment"}
              {mode === "email" && "Send confirmation email"}
            </h2>

            <p>
              {therapySession
                ? "View or update this therapy session."
                : "View or update this unavailable time."}
            </p>
          </div>

          <button type="button" className="modal-close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        {isLoading ? (
          <div className="modal-loading">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            {err && <div className="alert alert-danger">{err}</div>}

            {successMessage && (
              <div className="alert alert-success">{successMessage}</div>
            )}

            {mode === "view" && (
              <>
                <div className="appointment-view-card">
                  <div className="appointment-view-icon">
                    <CalendarCheck />
                  </div>

                  <div>
                    <h3>
                      {therapySession
                        ? clientName || "Therapy session"
                        : appointmentName || "Unavailable time"}
                    </h3>

                    <p>
                      {therapySession ? "Therapy session" : "Unavailable time"}
                    </p>
                  </div>
                </div>

                <div className="appointment-detail-list">
                  <div>
                    <CalendarCheck />
                    <span>Date</span>
                    <strong>{formatDate(start)}</strong>
                  </div>

                  <div>
                    <Clock />
                    <span>Time</span>
                    <strong>
                      {formatTime(start)} – {formatTime(finish)}
                    </strong>
                  </div>
                </div>

                <div className="modal-actions stacked">
                  <button
                    type="button"
                    className="btn modal-save-btn"
                    onClick={() => setMode("edit")}
                  >
                    <PencilSquare /> Edit appointment
                  </button>

                  {therapySession && clientId && (
                    <button
                      type="button"
                      className="btn modal-secondary-btn"
                      onClick={() => {
                        window.location.href = `/client/${clientId}`;
                      }}
                    >
                      View client
                    </button>
                  )}

                  {therapySession && (
                    <button
                      type="button"
                      className="btn modal-secondary-btn"
                      onClick={() => setMode("email")}
                    >
                      <Envelope /> Send confirmation email
                    </button>
                  )}

                  <button
                    type="button"
                    className="btn modal-danger-btn"
                    onClick={() => setMode("cancel")}
                  >
                    <Trash /> Cancel appointment
                  </button>
                </div>
              </>
            )}

            {mode === "edit" && (
              <form onSubmit={updateAppointment}>
                {!therapySession && (
                  <div className="mb-3">
                    <label className="form-label">Reason</label>
                    <input
                      className="form-control modal-input"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                  </div>
                )}

                {therapySession && (
                  <>
                    <div className="appointment-view-card">
                      <div className="appointment-view-icon">
                        <CalendarCheck />
                      </div>

                      <div>
                        <h3>{clientName || "Therapy session"}</h3>
                        <p>
                          Client cannot be changed here. Only the time is
                          updated.
                        </p>
                      </div>
                    </div>

                    <div className="form-check mb-3">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="sendEmail"
                        checked={sendEmail}
                        onChange={(e) => setSendEmail(e.target.checked)}
                      />

                      <label className="form-check-label" htmlFor="sendEmail">
                        Send confirmation email after saving changes
                      </label>
                    </div>
                  </>
                )}

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Start date</label>
                    <input
                      type="date"
                      className="form-control modal-input"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      required
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">Start time</label>
                    <select
                      className="form-control modal-input"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      required
                    >
                      <option value="">Select time</option>

                      {timeOptions.map((time) => (
                        <option key={time} value={time}>
                          {time}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Finish date</label>
                    <input
                      type="date"
                      className="form-control modal-input"
                      value={finishDate}
                      onChange={(e) => setFinishDate(e.target.value)}
                      required
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">Finish time</label>
                    <select
                      className="form-control modal-input"
                      value={finishTime}
                      onChange={(e) => setFinishTime(e.target.value)}
                      required
                    >
                      <option value="">Select time</option>

                      {timeOptions.map((time) => (
                        <option key={time} value={time}>
                          {time}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn modal-cancel-btn"
                    onClick={() => setMode("view")}
                  >
                    <XCircle /> Back
                  </button>

                  <button type="submit" className="btn modal-save-btn">
                    Save changes
                  </button>
                </div>
              </form>
            )}

            {mode === "email" && (
              <>
                <div className="appointment-view-card">
                  <div className="appointment-view-icon">
                    <Envelope />
                  </div>

                  <div>
                    <h3>Send appointment confirmation?</h3>

                    <p>
                      This will email the client the appointment date and time.
                    </p>
                  </div>
                </div>

                {!clientEmail && (
                  <div className="alert alert-warning">
                    This appointment does not include a client email. You may
                    need to add the email to the appointment response from the
                    backend, or send using the appointment client ID.
                  </div>
                )}

                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn modal-cancel-btn"
                    onClick={() => setMode("view")}
                  >
                    Back
                  </button>

                  <button
                    type="button"
                    className="btn modal-save-btn"
                    onClick={sendConfirmationEmail}
                  >
                    Send email
                  </button>
                </div>
              </>
            )}

            {mode === "cancel" && (
              <>
                <div className="appointment-cancel-card">
                  <div className="appointment-cancel-icon">
                    <Trash />
                  </div>

                  <div>
                    <h3>Cancel this appointment?</h3>

                    <p>
                      This will remove the appointment from your diary. This
                      cannot be undone.
                    </p>
                  </div>
                </div>

                {therapySession && (
                  <div className="form-check mb-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="sendCancelEmail"
                      checked={sendCancelEmail}
                      onChange={(e) => setSendCancelEmail(e.target.checked)}
                    />

                    <label
                      className="form-check-label"
                      htmlFor="sendCancelEmail"
                    >
                      Email client about this cancellation
                    </label>
                  </div>
                )}

                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn modal-cancel-btn"
                    onClick={() => setMode("view")}
                  >
                    Keep appointment
                  </button>

                  <button
                    type="button"
                    className="btn modal-danger-btn"
                    onClick={cancelAppointment}
                  >
                    Cancel appointment
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}